import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import api from '../../api';
import { useAuth } from '../../AuthContext';
import ApprovalGateway from '../../components/ApprovalGateway';
import InventoryPage from '../../components/InventoryPage';
import VoiceInterface from '../../components/VoiceInterface';
import OpsDashboard from '../../components/OpsDashboard';

function FarmersPanel() {
  const [vendors, setVendors] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/vendors?is_local=true'),
      api.get('/vendors/availability'),
    ]).then(([v, a]) => {
      setVendors(v.data);
      setAvailability(a.data);
    }).finally(() => setLoading(false));
  }, []);

  async function queryAll() {
    setSending(true);
    try {
      await api.post('/vendors/query', { vendor_ids: vendors.map(v => v.id) });
      setMsg('SMS sent to all local vendors!');
    } catch {
      setMsg('❌ Failed to send SMS');
    } finally {
      setSending(false);
    }
  }

  if (loading) return <div className="loading">Loading farmers...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold">Local Farmers & Fishermen ({vendors.length})</h3>
        <button className="btn btn-primary btn-sm" onClick={queryAll} disabled={sending}>
          {sending ? 'Sending...' : '📱 Query All via SMS'}
        </button>
      </div>
      {msg && <div className="alert alert-success mb-4">{msg}</div>}

      <div className="card mb-6">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Vendor</th><th>Type</th><th>Phone</th><th>Status</th></tr></thead>
            <tbody>
              {vendors.map(v => (
                <tr key={v.id}>
                  <td><strong>{v.name}</strong></td>
                  <td><span className="badge badge-teal">{v.type}</span></td>
                  <td>{v.contact_phone || '—'}</td>
                  <td><span className="badge badge-green">Active</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <h3 className="font-semibold mb-4">Recent Availability Replies</h3>
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Vendor</th><th>Item</th><th>Available</th><th>Unit</th><th>Received</th></tr></thead>
            <tbody>
              {availability.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#9ca3af' }}>No replies yet. Send SMS to farmers first.</td></tr>
              ) : availability.map(a => (
                <tr key={a.id}>
                  <td>{a.vendor_name || 'Unknown'}</td>
                  <td><strong>{a.item_name}</strong></td>
                  <td>{a.quantity_available ?? '?'}</td>
                  <td>{a.unit || '—'}</td>
                  <td className="text-gray text-sm">{new Date(a.received_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function GMDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ below_par: 0, pending_orders: 0, local_vendors: 0 });

  useEffect(() => {
    Promise.all([
      api.get('/inventory?below_par=true'),
      api.get('/orders?status=draft'),
      api.get('/vendors?is_local=true'),
    ]).then(([inv, orders, vendors]) => {
      setStats({ below_par: inv.data.length, pending_orders: orders.data.length, local_vendors: vendors.data.length });
    }).catch(() => {});
  }, []);

  return (
    <>
      <div className="page-header">
        <span className="page-title">GM Dashboard</span>
        <span className="text-sm text-gray">Welcome, {user?.name}</span>
      </div>
      <div className="page-body">
        <div className="stat-grid">
          <div className={`stat-card ${stats.below_par > 0 ? 'danger' : 'success'}`}>
            <div className="stat-label">Items Below Par</div>
            <div className="stat-value">{stats.below_par}</div>
          </div>
          <div className={`stat-card ${stats.pending_orders > 0 ? 'warning' : ''}`}>
            <div className="stat-label">Pending Approvals</div>
            <div className="stat-value">{stats.pending_orders}</div>
          </div>
          <div className="stat-card success">
            <div className="stat-label">Local Vendors</div>
            <div className="stat-value">{stats.local_vendors}</div>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <WeatherWidget />
          <WeatherPredictions />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          {[
            { path: '/gm/orders', icon: '✅', label: 'Approve Order' },
            { path: '/gm/farmers', icon: '🌾', label: 'Farmers' },
            { path: '/gm/voice', icon: '🎙️', label: 'Voice Query' },
          ].map(item => (
            <button key={item.path} className="card" style={{ border: 'none', cursor: 'pointer', padding: 20, textAlign: 'center' }} onClick={() => navigate(item.path)}>
              <div style={{ fontSize: 36, marginBottom: 6 }}>{item.icon}</div>
              <div className="font-semibold text-sm">{item.label}</div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

export default function GMView() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route index element={<OpsDashboard basePath="/gm" />} />
      <Route path="orders" element={<><div className="page-header"><span className="page-title">Approve Order</span></div><div className="page-body"><ApprovalGateway /></div></>} />
      <Route path="inventory" element={<InventoryPage title="Full Inventory" />} />
      <Route path="farmers" element={<><div className="page-header"><span className="page-title">Local Farmers</span></div><div className="page-body"><FarmersPanel /></div></>} />
      <Route path="voice" element={<><div className="page-header"><span className="page-title">Voice</span></div><div className="page-body"><VoiceInterface restaurantId={user?.restaurant_id} /></div></>} />
    </Routes>
  );
}
